import { api } from "@/lib/api";
import type {
  CapacitacionesDocumento,
  CapacitacionesDocumentoNoAplicaPor,
  CapacitacionesDocumentoSlot,
} from "@/types/capacitaciones-documento";
import type { EmpresaDocumentoTipo } from "@/types/empresa-documento-tipo";

/**
 * Puerto tipado para el módulo "Capacitaciones" (documentos institucionales
 * de empresa). Espeja el patrón de `persona-documentos-adapter.ts`
 * (`listTipos` + `listDocumentos` separados, unidos en el panel por
 * `tipo_documento_id`) contra el contrato ya expuesto por fc-api (B1+B3):
 *   GET    /me/empresa/documentos/tipos
 *   GET    /me/empresa/documentos                 (N slots, uno por tipo del catálogo)
 *   POST   /me/empresa/documentos/{slug}           (multipart, reemplaza el slot)
 *   GET    /me/empresa/documentos/{slug}/download
 *   DELETE /me/empresa/documentos/{slug}
 *   PATCH  /me/empresa/documentos/{slug}/usar-para-capacitaciones
 *   PATCH  /me/empresa/documentos/{slug}/no-aplica      (slice A3)
 *   PATCH  /me/empresa/documentos/{slug}/vencimiento    (slice A1/A3)
 *
 * Nota clave: las acciones (subir/descargar/eliminar) se resuelven por
 * SLUG en la URL, no por id — el llamador (panel) siempre tiene el `tipo`
 * (o el `slot`) en mano y pasa `.slug`.
 */
export interface CapacitacionesDocumentosUploadOptions {
  /** Reporta el progreso de subida (0-100) cuando el navegador lo soporta. */
  onProgress?: (percent: number) => void;
  /** Permite cancelar la subida en curso (botón "Cancelar" del slot). */
  signal?: AbortSignal;
}

/** Igual forma que `RecursoDocumentosVencimientoOptions` — mismo contrato de vigencia en los tres módulos. */
export interface CapacitacionesDocumentosVencimientoOptions {
  /** `null` = "Sin vencimiento". Fecha ISO `YYYY-MM-DD` cuando sí vence. */
  fechaVencimiento: string | null;
  /** Subconjunto de {7, 15, 30}. Solo tiene efecto si `fechaVencimiento` no es null. */
  notificarDias: number[];
}

/** Respuesta de `PATCH {slug}/no-aplica` — no reusa `CapacitacionesDocumento` porque un slot marcado nunca tiene archivo. */
export interface CapacitacionesDocumentoNoAplicaResult {
  tipo_documento_id: number;
  slug: string;
  no_aplica: boolean;
  no_aplica_at: string | null;
  no_aplica_por: CapacitacionesDocumentoNoAplicaPor | null;
}

export interface CapacitacionesDocumentosAdapter {
  listTipos(): Promise<EmpresaDocumentoTipo[]>;
  /** Solo los slots CON documento subido (id !== null) — el catálogo completo
   * lo entrega `listTipos`, el join se hace client-side por `tipo_documento_id`
   * (mismo patrón que `persona-documentos-adapter`). */
  listDocumentos(): Promise<CapacitacionesDocumento[]>;
  /**
   * Slice A3/A4 — un slot por tipo del catálogo (con o sin archivo), con la
   * marca de "No aplica" incluida. Una sola llamada reemplaza a
   * `listTipos()+listDocumentos()` para las pantallas que necesitan el
   * catálogo completo (Pantalla 1): la marca de "No aplica" solo existe en
   * slots SIN archivo, así que `listDocumentos()` (que filtra por
   * `id !== null`) no puede exponerla.
   */
  listSlots(): Promise<CapacitacionesDocumentoSlot[]>;
  upload(
    slug: string,
    file: File,
    opts?: CapacitacionesDocumentosUploadOptions,
  ): Promise<CapacitacionesDocumento>;
  /** Recibe el `documento` completo para poder resolver un nombre de archivo
   * de respaldo si el servidor no expone `Content-Disposition` (ver
   * `api.blobWithFilename`). */
  download(slug: string, documento: CapacitacionesDocumento): Promise<void>;
  /**
   * "Exportar respaldo" — un único ZIP con TODOS los documentos cargados de
   * la empresa, agrupados por el `grupo` del catálogo. El nombre del archivo
   * lo resuelve el servidor (Content-Disposition); acá no se adivina.
   */
  exportarZip(): Promise<void>;
  destroy(slug: string): Promise<void>;
  /** Fase A — marca/desmarca el documento como candidato a capacitaciones IA.
   * Devuelve el documento actualizado. 404 si el slot no tiene documento. */
  setUsarParaCapacitaciones(slug: string, value: boolean): Promise<CapacitacionesDocumento>;
  /**
   * Slice A3 — marca/desmarca un tipo como "No exigible" para esta empresa.
   * 422 si el slot ya tiene un archivo subido (hay que eliminarlo primero).
   * Idempotente del lado del backend.
   */
  setNoAplica(slug: string, value: boolean): Promise<CapacitacionesDocumentoNoAplicaResult>;
  /**
   * Slice A1/A3 — actualiza SOLO fecha_vencimiento + notificar_dias de un
   * documento ya subido, sin re-subir archivo ("renovar sin recargar").
   * 404 si el slot no tiene documento.
   */
  updateVencimiento(
    slug: string,
    opts: CapacitacionesDocumentosVencimientoOptions,
  ): Promise<CapacitacionesDocumento>;
}

/** Shape crudo del índice — un slot por tipo del catálogo, con metadata del
 * documento subido o `null` si el slot está vacío. */
interface ApiEmpresaDocumentoSlot {
  id: number | null;
  tipo_documento_id: number;
  slug: string;
  label: string;
  grupo: string;
  no_aplica: boolean;
  no_aplica_at: string | null;
  no_aplica_por: CapacitacionesDocumentoNoAplicaPor | null;
  filename_original: string | null;
  size_bytes: number | null;
  mime_type: string | null;
  usar_para_capacitaciones: boolean | null;
  fecha_vencimiento: string | null;
  notificar_dias: number[] | null;
  estado_vencimiento: CapacitacionesDocumento["estado_vencimiento"] | null;
  uploaded_at: string | null;
  uploaded_by: CapacitacionesDocumento["uploaded_by"];
}

function slotADocumento(slot: ApiEmpresaDocumentoSlot): CapacitacionesDocumento | null {
  if (slot.id === null) return null;
  return {
    id: slot.id,
    tipo_documento_id: slot.tipo_documento_id,
    filename_original: slot.filename_original as string,
    size_bytes: slot.size_bytes as number,
    mime_type: slot.mime_type as string,
    usar_para_capacitaciones: slot.usar_para_capacitaciones ?? false,
    fecha_vencimiento: slot.fecha_vencimiento,
    notificar_dias: slot.notificar_dias ?? [],
    estado_vencimiento: slot.estado_vencimiento ?? "sin_vencimiento",
    uploaded_at: slot.uploaded_at as string,
    uploaded_by: slot.uploaded_by,
  };
}

export const apiCapacitacionesDocumentosAdapter: CapacitacionesDocumentosAdapter = {
  async listTipos() {
    const res = await api.get<{ data: EmpresaDocumentoTipo[] }>("/me/empresa/documentos/tipos");
    return res.data;
  },

  async listDocumentos() {
    const res = await api.get<{ data: ApiEmpresaDocumentoSlot[] }>("/me/empresa/documentos");
    return res.data
      .filter((slot) => slot.id !== null)
      .map((slot) => slotADocumento(slot) as CapacitacionesDocumento);
  },

  async listSlots() {
    const res = await api.get<{ data: ApiEmpresaDocumentoSlot[] }>("/me/empresa/documentos");
    return res.data.map((slot) => ({
      tipo_documento_id: slot.tipo_documento_id,
      slug: slot.slug,
      label: slot.label,
      grupo: slot.grupo,
      no_aplica: slot.no_aplica,
      no_aplica_at: slot.no_aplica_at,
      no_aplica_por: slot.no_aplica_por,
      documento: slotADocumento(slot),
    }));
  },

  async upload(slug, file, opts) {
    const fd = new FormData();
    fd.append("archivo", file);
    const res = await api.uploadForm<{ data: CapacitacionesDocumento }>(
      `/me/empresa/documentos/${slug}`,
      fd,
      { onProgress: opts?.onProgress, signal: opts?.signal },
    );
    return res.data;
  },

  async download(slug, documento) {
    // El nombre de descarga lo define el SERVER (Content-Disposition). En una
    // descarga por blob el atributo `a.download` gana sobre el header, así
    // que leemos el nombre del server con `blobWithFilename` y sólo caemos a
    // `filename_original` si no llegara (mismo patrón que persona-documentos).
    const { blob, filename } = await api.blobWithFilename(
      `/me/empresa/documentos/${slug}/download`,
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename ?? documento.filename_original;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke diferido: algunos navegadores tratan la descarga disparada por
    // `a.click()` como asíncrona y revocar el object URL de inmediato puede
    // cancelarla a mitad de camino.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  async exportarZip() {
    // Mismo patrón blob → object URL → <a> sintético que `download()` de
    // acá arriba y que `descargarZip()` de la Ficha del Trabajador, incluido
    // el revoke diferido. El fallback del nombre sólo se usa si el header no
    // llegara (p. ej. un origen cruzado sin `Access-Control-Expose-Headers`).
    const { blob, filename } = await api.blobWithFilename("/me/empresa/documentos/zip");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename ?? "documentos_empresa.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  async destroy(slug) {
    await api.deleteJson(`/me/empresa/documentos/${slug}`);
  },

  async setUsarParaCapacitaciones(slug, value) {
    const res = await api.patch<{ data: CapacitacionesDocumento }>(
      `/me/empresa/documentos/${slug}/usar-para-capacitaciones`,
      { usar_para_capacitaciones: value },
    );
    return res.data;
  },

  async setNoAplica(slug, value) {
    const res = await api.patch<{ data: CapacitacionesDocumentoNoAplicaResult }>(
      `/me/empresa/documentos/${slug}/no-aplica`,
      { no_aplica: value },
    );
    return res.data;
  },

  async updateVencimiento(slug, opts) {
    const res = await api.patch<{ data: CapacitacionesDocumento }>(
      `/me/empresa/documentos/${slug}/vencimiento`,
      { fecha_vencimiento: opts.fechaVencimiento, notificar_dias: opts.notificarDias },
    );
    return res.data;
  },
};
